import React, {useEffect, useState} from 'react';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import WebSearchSetting from "./WebSearchSetting";
import {CampaignService} from "../../service/CampaignService";
import {Panel} from "primereact/panel";
import JobSchedulingTimeSetting from "./JobSchedulingTimeSetting";
import {AppContext} from "../../HomePage";
import {Loading, IndexTemplate} from "../../utils/InlineComponents";
import {MultiSelect} from "primereact/multiselect";
import {Dropdown} from "primereact/dropdown";
import {SpecialFieldService} from "../../service/SpecialFieldService";
import {search_fields} from "../../utils/Constants";
import {normalizeHostSearchConfig} from "./JobSchedulingSiteSearchSetting";

/**
 * The daily job's own `search_fields_based_url[host]` record is independent
 * from the (non-daily) site-search job's block and CAN diverge once the user
 * picks a value here. To stop it diverging by accident before that happens,
 * a host with no target stored in the daily block inherits the value stored
 * for that same host in the site-search block, falling back to 'competitor'.
 * This only affects what is displayed/seeded — it never writes into the
 * daily block on its own.
 */
export const resolveDailyTarget = (dailyRecord, siteSearchRecord) => {
    const daily = normalizeHostSearchConfig(dailyRecord);
    if (daily.target)
        return daily.target;
    return normalizeHostSearchConfig(siteSearchRecord).target || 'competitor';
};

// Every write of a daily host record goes through the shared normalizer, so
// a `fields` list is always present and any key already on the record
// (including a previously chosen `target`) survives a partial update.
export const writeDailyHostRecord = (existing, changes) => ({
    ...normalizeHostSearchConfig(existing),
    ...changes,
});

const JobSchedulingDailySiteSearchSetting = (props) => {

    const {t, setError} = React.useContext(AppContext);
    const [loading, setLoading] = useState(false);


    const [campaign, setCampaign] = useState(props.campaign);
    const [searchCompetitors, setSearchCompetitors] = useState(campaign.settings && campaign.settings.daily_site_search_job && campaign.settings.daily_site_search_job.competitor_urls ? [...campaign.settings.daily_site_search_job.competitor_urls.map(c => ({url: c}))] : []);
    function getSelectedFieldNames(fields) {
        return Array.isArray(fields) ? fields.map(field => field.name ? field.name : field) : [];
    }

    function getSelectedFieldCodes(fields) {
        return Array.isArray(fields) ? fields.map(field => field.code ? field.code : field) : [];
    }

    const [campaignCompetitors, setCampaignCompetitors] = useState(() => {
        const competitors = campaign.settings?.daily_site_search_job?.competitor_urls || [];
        const searchFieldsMap = campaign.settings?.daily_site_search_job?.search_fields_based_url || {};
        const matchFieldsMap = campaign.settings?.daily_site_search_job?.match_fields_based_url || {};
        const siteSearchFieldsMap = campaign.settings?.site_search_job?.search_fields_based_url || {};

        return competitors.map(url => ({
            url,
            selected_fields: getSelectedFieldCodes(searchFieldsMap[url]?.fields || searchFieldsMap[url]),
            selected_match_fields: getSelectedFieldNames(matchFieldsMap[url]?.fields || matchFieldsMap[url]),
            target: resolveDailyTarget(searchFieldsMap[url], siteSearchFieldsMap[url])
        }));
    });
    const [matchFields, setMatchFields] = useState([]);
    const campaignService = new CampaignService();
    const specialFieldService = new SpecialFieldService();
    let daily_site_search_job = campaign.settings && campaign.settings.daily_site_search_job ? campaign.settings.daily_site_search_job : {}

    const getMatchFields = (specialFields) => {
        const productCodes = [{name: 'sku'}, {name: 'mpn'}, {name: 'ean'}, {name: 'upc'}, {name: 'asin'}, {name: 'barcode'}, {name: 'search_key'}, {name: 'brand'}, {name: 'category'}];
        return productCodes.concat(specialFields || []);
    }

    useEffect(() => {
        campaign.settings.daily_site_search_job = daily_site_search_job
        if (campaign.id) {
            setLoading(true);

            Promise.all([
                campaignService.getCampaignCompetitors(campaign.id),
                specialFieldService.getSpecialFields()
            ])
                .then(([competitors, specialFields]) => {

                    const siteSearchJob = campaign.settings?.daily_site_search_job || {};
                    const searchFieldsMap = siteSearchJob.search_fields_based_url || {};
                    const matchFieldsMap = siteSearchJob.match_fields_based_url || {};
                    const siteSearchFieldsMap = campaign.settings?.site_search_job?.search_fields_based_url || {};

                    const prepared = competitors
                        .filter(comp => comp.is_site_searching_ready)
                        .map(c => ({
                            ...c,
                            selected_fields: getSelectedFieldCodes(searchFieldsMap[c.url]?.fields || searchFieldsMap[c.url]),
                            selected_match_fields: getSelectedFieldNames(matchFieldsMap[c.url]?.fields || matchFieldsMap[c.url]),
                            target: resolveDailyTarget(searchFieldsMap[c.url], siteSearchFieldsMap[c.url])
                        }));

                    setMatchFields(getMatchFields(specialFields));
                    setCampaignCompetitors(prepared);
                    setLoading(false);
                })
                .catch(error => {
                    setError(error);
                    setError(null);
                    setLoading(false);
                });
        }
    }, []);

    const urlTemplate = (rowData) => <a target="_blank" rel="noopener noreferrer" href={rowData.url}>{rowData.url}</a>;
    const searchFieldsTemplate = (rowData) => (
        <div onMouseDown={(e) => e.stopPropagation()}>
            <MultiSelect
                value={Array.isArray(rowData.selected_fields) ? rowData.selected_fields : []}
                options={search_fields}
                optionLabel="name"
                optionValue="code"
                onChange={(e) => handleSearchFieldChange(rowData.url, e.value)}
            />
        </div>
    );

    const matchFieldsTemplate = (rowData) => (
        <div onMouseDown={(e) => e.stopPropagation()}>
            <MultiSelect
                value={Array.isArray(rowData.selected_match_fields) ? rowData.selected_match_fields : []}
                options={matchFields}
                optionLabel="name"
                optionValue="name"
                onChange={(e) => handleMatchFieldChange(rowData.url, e.value)}
            />
        </div>
    );

    const handleSearchFieldChange = (url, value) => {
        const selectedValues = Array.isArray(value) ? value : [];

        setCampaignCompetitors(prev =>
            prev.map(item =>
                item.url === url
                    ? { ...item, selected_fields: selectedValues }
                    : item
            )
        );

        if (!campaign.settings.daily_site_search_job.search_fields_based_url) {
            campaign.settings.daily_site_search_job.search_fields_based_url = {};
        }

        campaign.settings.daily_site_search_job.search_fields_based_url[url] = writeDailyHostRecord(
            campaign.settings.daily_site_search_job.search_fields_based_url[url],
            {
                selected: searchCompetitors.some(s => s.url === url),
                fields: selectedValues.map(code => {
                    const found = search_fields.find(f => f.code === code);
                    return found || { code };
                })
            }
        );
    };

    const targetOptions = [
        {value: 'competitor', label: t('suggestion.target_competitor')},
        {value: 'supplier', label: t('suggestion.target_supplier')}
    ];

    const targetTemplate = (rowData) => (
        <div onMouseDown={(e) => e.stopPropagation()}>
            <Dropdown
                value={rowData.target || 'competitor'}
                options={targetOptions}
                onChange={(e) => handleTargetChange(rowData.url, e.value)}
            />
        </div>
    );

    const handleTargetChange = (url, value) => {
        setCampaignCompetitors(prev =>
            prev.map(item =>
                item.url === url
                    ? { ...item, target: value }
                    : item
            )
        );

        if (!campaign.settings.daily_site_search_job.search_fields_based_url) {
            campaign.settings.daily_site_search_job.search_fields_based_url = {};
        }

        campaign.settings.daily_site_search_job.search_fields_based_url[url] = writeDailyHostRecord(
            campaign.settings.daily_site_search_job.search_fields_based_url[url],
            { selected: searchCompetitors.some(s => s.url === url), target: value }
        );
    };

    const handleMatchFieldChange = (url, value) => {
        const selectedValues = Array.isArray(value) ? value : [];

        setCampaignCompetitors(prev =>
            prev.map(item =>
                item.url === url
                    ? { ...item, selected_match_fields: selectedValues }
                    : item
            )
        );

        if (!campaign.settings.daily_site_search_job.match_fields_based_url) {
            campaign.settings.daily_site_search_job.match_fields_based_url = {};
        }

        campaign.settings.daily_site_search_job.match_fields_based_url[url] = {
            selected: searchCompetitors.some(s => s.url === url),
            fields: selectedValues.map(name => {
                const found = matchFields.find(f => f.name === name);
                return found || { name };
            })
        };
    };

    return (
        <div className="p-grid p-fluid">
            <Panel header={t('suggestion.time_settings')} style={{height:'100%'}}>
                <JobSchedulingTimeSetting job={daily_site_search_job} isDaily={true}/>
            </Panel>

            <p></p>

            <Panel header={t('campaign.generic_setting')} style={{height:'100%'}}>
                <WebSearchSetting webSearchSetting={daily_site_search_job}/>
            </Panel>

            <p></p>
            {loading ?
                <div className="p-col-12" style={{textAlign: 'center'}}>
                    <Loading/>
                </div> :
                <Panel header={t('suggestion.competitor_settings')} style={{height:'100%'}}>
                    <div className="p-col-12 p-xl-12">
                        <DataTable
                            value={campaignCompetitors}
                            dataKey="url"
                            selection={searchCompetitors}
                            onSelectionChange={e => {
                                const selected = e.value.map(s => s.url);

                                setSearchCompetitors(e.value);

                                daily_site_search_job.competitor_urls = selected;

                                if (!campaign.settings.daily_site_search_job.search_fields_based_url) {
                                    campaign.settings.daily_site_search_job.search_fields_based_url = {};
                                }

                                if (!campaign.settings.daily_site_search_job.match_fields_based_url) {
                                    campaign.settings.daily_site_search_job.match_fields_based_url = {};
                                }

                                campaignCompetitors.forEach(c => {
                                    const existingFields = campaign.settings.daily_site_search_job.match_fields_based_url?.[c.url]?.fields ||
                                        campaign.settings.daily_site_search_job.match_fields_based_url?.[c.url] ||
                                        [];

                                    campaign.settings.daily_site_search_job.search_fields_based_url[c.url] = writeDailyHostRecord(
                                        campaign.settings.daily_site_search_job.search_fields_based_url?.[c.url],
                                        { selected: selected.includes(c.url) }
                                    );

                                    campaign.settings.daily_site_search_job.match_fields_based_url[c.url] = {
                                        selected: selected.includes(c.url),
                                        fields: existingFields
                                    };
                                });
                            }}
                            selectionMode="checkbox"
                        >
                            <Column selectionMode="multiple" style={{ width: '3em' }} />
                            <Column header={t('table_headers.index')} body={IndexTemplate} />
                            <Column field="url" body={urlTemplate} header={t('campaign.competitors.url')} />
                            <Column body={searchFieldsTemplate} header={t('suggestion.search_fields')} />
                            <Column body={matchFieldsTemplate} header={t('suggestion.match_fields')} />
                            {campaign.settings?.supplier_module?.is_active &&
                                <Column body={targetTemplate} header={t('suggestion.target')} />}
                        </DataTable>
                    </div>
                </Panel>
            }
        </div>
    );

};
export default (JobSchedulingDailySiteSearchSetting)
